import { Attribute } from '../../Model/Attribute';
import { ObjectUtility } from '../../Service/ObjectUtility';
import { OptionsInterface } from './AbstractFilterView';
import { FilterOption, FilterOptionsMap } from './FilterOption';

export class OptionsCollection<OPTION_TYPE> {
    public static from<OPTION_TYPE>(input: Attribute[] | string[] | OptionsInterface | OptionsCollection<OPTION_TYPE>): OptionsCollection<OPTION_TYPE> {
        if (input instanceof OptionsCollection) {
            return input;
        }
        if (!Array.isArray(input)) {
            return OptionsCollection.fromObject(input);
        }
        if (input.length === 0) {
            return new OptionsCollection([]);
        }
        if (input[0] instanceof Attribute) {
            return OptionsCollection.fromAttributesArray(input as Attribute[]);
        } else if (typeof input[0] === 'string') {
            return OptionsCollection.fromStringArray(input as string[]);
        } else {
            throw new TypeError('Could not handle input type');
        }
    }

    public static fromAttributesArray<OPTION_TYPE>(attributes: Attribute[]): OptionsCollection<OPTION_TYPE> {
        let options: FilterOptionsMap<OPTION_TYPE> = {};
        for (const attribute of attributes) {
            options = Object.assign(options, FilterOption.fromAttribute(attribute));
        }

        return new OptionsCollection(Object.values(options));
    }

    public static fromStringArray<OPTION_TYPE>(options: string[]): OptionsCollection<OPTION_TYPE> {
        return new OptionsCollection(
            ObjectUtility.unique(options).map(
                key => new FilterOption(key, key, true, false)
            )
        );
    }

    public static fromObject<OPTION_TYPE>(options: OptionsInterface): OptionsCollection<OPTION_TYPE> {
        return new OptionsCollection(
            Object.keys(options).map(
                key => new FilterOption(key, options[key], true, false)
            )
        );
    }

    private readonly _options: FilterOption<OPTION_TYPE>[];

    constructor(options: FilterOption<OPTION_TYPE>[]) {
        this._options = options;
    }

    public get length(): number {
        return this._options.length;
    }

    public getOptions(): FilterOption<OPTION_TYPE>[] {
        return this._options;
    }

    public contains(needle: string | FilterOption<OPTION_TYPE>): boolean {
        if (needle instanceof FilterOption) {
            return this.contains(needle.name);
        }
        return typeof this._options.find(o => o.name === needle) !== 'undefined';
    }

    public map<R>(callback: (o: FilterOption<OPTION_TYPE>) => R) {
        return this._options.map(callback);
    }

    public keys(): string[] {
        return this.map(o => o.name);
    }

    public sort(compareFn?: (
        a: FilterOption<OPTION_TYPE>,
        b: FilterOption<OPTION_TYPE>
    ) => number): OptionsCollection<OPTION_TYPE> {
        return new OptionsCollection(this._options.slice().sort(compareFn));
    }

    /**
     * Sort the options by their label using `localeCompare()`
     */
    public localeSort(): OptionsCollection<OPTION_TYPE> {
        return this.sort((a, b) => a.label.localeCompare(b.label));
    }
}
